import { Skeleton, Flex, Spacer, Box, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  analytics as commonAnalytics,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  CreditFacilityGQLType,
  GetDailyTotalRiskInsightsQueryGQLType,
  TotalRiskInsightsOutputDataGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartCards } from '../shared/PortfolioChartCards';
import { PortfolioChartWithCardsTemplateForOrganization } from '../shared/PortfolioChartTemplate';
import {
  aggregateBorrowingBaseRiskDataForSelectedCreditFacilities,
  AggregateBorrowingRiskChartData,
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartComponentType,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { generateLtvChartConfig } from './portfolioRiskChartConfigs';
import { PortfolioRiskTableTemplate } from './PortfolioRiskTableTemplate';
import {
  formatRiskTableColumns,
  formatRiskTablesDataMap,
  getCreditFacilitySlugsForIds,
  RiskFieldToTableRowMap,
} from './PortfolioRiskTableUtils';
import { colors } from 'packages/common-frontend/colors';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetDailyTotalRiskInsightsQuery } from 'packages/portfolio/requests/requests.gql.generated';

export const PortfolioLtvChartTemplateForOrganization: React.FC<{
  organizationId: string;
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[];
}> = withLDConsumer()(({ flags, organizationId, creditFacilities }) => {
  const toast = useToast();
  const history = useHistory();

  const initialSelectedChartComponentType: PortfolioChartComponentType = 'line';
  const initialChartConfig = generateLtvChartConfig({
    chartComponentType: initialSelectedChartComponentType,
  });
  const initialActiveFields = getInitialActiveFields({
    chartConfig: initialChartConfig,
  }) as (keyof AggregateBorrowingRiskChartData)[];

  const initialSelectedCreditFacilityIds = _.map(
    creditFacilities,
    ({ id }) => id,
  );

  const initialCreditFacilitySelectOptions: {
    label: string;
    value: string;
  }[] = useMemo(
    () =>
      _.map(creditFacilities, (creditFacility) => {
        return {
          value: creditFacility.id,
          label: commonUtils.creditFacilitySlugToName(creditFacility.slug),
        };
      }),
    [creditFacilities],
  );

  const initialRiskTablesDataMap: RiskFieldToTableRowMap = {
    collateralCovenantLtv: [],
    netOutstandingLtv: [],
    performingLtv: [],
    chargedOffPercentageOfTotalExposure: [],
    delinquentPercentageOfTotalExposure: [],
  };

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<AggregateBorrowingRiskChartData | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  const [
    riskInputData,
    setRiskInputData,
  ] = useState<TotalRiskInsightsOutputDataGQLType>({
    borrowingBaseRisk: [],
    receivableRisk: [],
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    AggregateBorrowingRiskChartData[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    AggregateBorrowingRiskChartData[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    (keyof AggregateBorrowingRiskChartData)[]
  >(initialActiveFields);

  const [selectedCreditFacilityIds, setSelectedCreditFacilityIds] = useState<
    string[]
  >(initialSelectedCreditFacilityIds);

  const [
    selectedChartComponentType,
    setSelectedChartComponentType,
  ] = useState<PortfolioChartComponentType>(initialSelectedChartComponentType);
  const [chartConfig, setChartConfig] = useState<
    PortfolioChartConfig<keyof AggregateBorrowingRiskChartData>
  >(initialChartConfig);

  /**
   * Risk tables data state management:
   * All of the filters (cf select, date range, time period) apply to all tables
   * on the risk page
   */
  const [riskTableColumns, setRiskTableColumns] = useState<
    { accessorKey: string; header: string }[]
  >([]);
  const [
    riskTablesDataMap,
    setRiskTablesDataMap,
  ] = useState<RiskFieldToTableRowMap>(initialRiskTablesDataMap);

  useEffect(() => {
    const updatedRiskTableColumns = formatRiskTableColumns({
      chartDataWithDateRangeAndTimePeriod: chartDataWithTimePeriod,
    });
    const updatedRiskTablesDataMap = formatRiskTablesDataMap({
      chartDataWithDateRangeAndTimePeriod: chartDataWithTimePeriod,
      dailyBorrowingBaseRiskData: riskInputData.borrowingBaseRisk,
      dailyReceivableRiskData: riskInputData.receivableRisk,
      requestedCreditFacilitySlugs: getCreditFacilitySlugsForIds(
        selectedCreditFacilityIds,
        creditFacilities,
      ),
    });

    setRiskTableColumns(updatedRiskTableColumns);
    setRiskTablesDataMap(updatedRiskTablesDataMap);
  }, [
    chartDataWithTimePeriod,
    creditFacilities,
    selectedCreditFacilityIds,
    riskInputData,
  ]);

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<AggregateBorrowingRiskChartData>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  const {
    refetch: refetchRiskInsights,
    data: riskInsightsData,
    loading: isRiskInsightsLoading,
  } = useGetDailyTotalRiskInsightsQuery({
    variables: {
      organizationParams: {
        organizationId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const {
        riskData: { borrowingBaseRisk, receivableRisk },
      } = data;

      const extendedBorrowingBaseRiskData = commonAnalytics.generateExtendedData(
        borrowingBaseRisk,
        { baseDateStrKey: 'reportingDate' },
        ['fillGroupData', 'extendEnd', 'fillDate'],
        {
          extendEndDateStr: '2023-11-02',
          dataGroupKey: 'creditFacilityData',
          identifierIdKey: 'creditFacilityId',
        },
      );

      const extendedReceivableRiskData = commonAnalytics.generateExtendedData(
        receivableRisk,
        { baseDateStrKey: 'reportingDate' },
        ['fillGroupData', 'extendEnd', 'fillDate'],
        {
          extendEndDateStr: '2023-11-02',
          dataGroupKey: 'creditFacilityData',
          identifierIdKey: 'creditFacilityId',
        },
      );

      // set the daily input data for all credit facilities to be used in later filters
      // without having to make another api call for the full input set
      setRiskInputData({
        receivableRisk: extendedReceivableRiskData,
        borrowingBaseRisk: extendedBorrowingBaseRiskData,
      });

      // first filter the data and run aggregation on only the selected credit facilities
      const riskDataForSelectedFacilities = aggregateBorrowingBaseRiskDataForSelectedCreditFacilities(
        {
          selectedCreditFacilityIds,
          dailyBorrowingBaseRiskData: extendedBorrowingBaseRiskData,
        },
      );

      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: riskDataForSelectedFacilities,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
          {
            chartData: riskDataForSelectedFacilities,
            selectedTimePeriod,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: creditAgreementFullArtifactsData,
    loading: isCreditAgreementFullArtifactsLoading,
  } = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery({
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (
    isRiskInsightsLoading ||
    !riskInsightsData ||
    !creditAgreementFullArtifactsData ||
    isCreditAgreementFullArtifactsLoading
  ) {
    return <Skeleton height="700px" />;
  }

  const {
    organization: { currentCreditAgreementArtifacts },
  } = creditAgreementFullArtifactsData;

  let parsedCreditAgreementFullArtifacts = null;

  try {
    parsedCreditAgreementFullArtifacts = _.map(
      currentCreditAgreementArtifacts,
      (creditAgreementFullArtifact) => {
        const { creditAgreement, id } = creditAgreementFullArtifact;
        const parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        finleyAssert(
          parsedCreditAgreement,
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
        );
        return parsedCreditAgreement;
      },
    );
  } catch (err) {
    Sentry.captureException(err);
    const errorToastTitle = `Error parsing credit agreement artifacts for organization ${organizationId} while loading portfolio LTV charts`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    history.push('/');
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
      {
        chartData: chartDataForDateRange,
        selectedTimePeriod: timePeriod,
      },
    );

    setChartDataWithTimePeriod(formattedChartData);
  };

  const onSelectCreditFacilityIds = (creditFacilityIds: string[]) => {
    setSelectedCreditFacilityIds(creditFacilityIds);

    // first filter the data and run aggregation on only the selected credit facilities
    const riskDataForSelectedFacilities = aggregateBorrowingBaseRiskDataForSelectedCreditFacilities(
      {
        selectedCreditFacilityIds: creditFacilityIds,
        dailyBorrowingBaseRiskData: riskInputData.borrowingBaseRisk,
      },
    );

    // set base chart data for date range with the newly aggregated data
    const formattedChartData = formatChartDataByReportingDate({
      chartData: riskDataForSelectedFacilities,
      selectedTimePeriod: ChartTimePeriod.day,
    });
    setChartDataForDateRange(formattedChartData);

    // then set data for the specific time period
    if (selectedTimePeriod !== ChartTimePeriod.day) {
      const formattedChartDataForPeriod = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
        {
          chartData: riskDataForSelectedFacilities,
          selectedTimePeriod,
        },
      );
      setChartDataWithTimePeriod(formattedChartDataForPeriod);
    } else {
      setChartDataWithTimePeriod(formattedChartData);
    }
  };

  const onSelectChartComponentType = (
    chartComponentType: PortfolioChartComponentType,
  ) => {
    setSelectedChartComponentType(chartComponentType);

    const newChartConfig = generateLtvChartConfig({ chartComponentType });
    setChartConfig(newChartConfig);
  };

  const PortfolioChartTemplateInstance = PortfolioChartWithCardsTemplateForOrganization<GetDailyTotalRiskInsightsQueryGQLType>();
  const PortfolioChartInstance = PortfolioReChart<AggregateBorrowingRiskChartData>();
  const PortfolioChartCardsInstance = PortfolioChartCards<AggregateBorrowingRiskChartData>();

  return (
    <Box
      mb="40px"
      backgroundColor="white"
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      padding="40px"
    >
      <PortfolioChartTemplateInstance
        organizationId={organizationId}
        refetch={refetchRiskInsights}
        chartDataDateRange={chartDataDateRange}
        setChartDataDateRange={setChartDataDateRange}
        selectedTimePeriod={selectedTimePeriod}
        onSelectTimePeriod={onSelectTimePeriod}
        selectedCreditFacilityIds={selectedCreditFacilityIds}
        onSelectCreditFacilityIds={onSelectCreditFacilityIds}
        selectedChartComponentType={selectedChartComponentType}
        onSelectChartComponentType={onSelectChartComponentType}
        initialCreditFacilitySelectOptions={initialCreditFacilitySelectOptions}
      >
        {/* child 1 */}
        <PortfolioChartInstance
          chartData={chartDataWithTimePeriod}
          chartTitle="LTV"
          selectedTimePeriod={selectedTimePeriod}
          currentSelectedBarReportingDate={
            selectedBar
              ? selectedBar.reportingDate
              : getDefaultDashboardDisplayEndDate()
          }
          onSelectBar={setSelectedBar}
          chartConfig={chartConfig}
          visibleChartComponents={activeFields}
          // lowest y axis domain is 0% for LTV
          leftYAxisDomain={[0, 'auto']}
        />

        {/* child 2 */}

        {selectedBar && (
          <Flex width="25%" flexDirection="column">
            <PortfolioChartCardsInstance
              selectedBarData={selectedBar}
              chartConfig={chartConfig}
              activeFields={activeFields}
              onToggleActiveField={(
                selectedField: keyof AggregateBorrowingRiskChartData,
              ) =>
                onToggleActiveField<AggregateBorrowingRiskChartData>({
                  selectedField,
                  currentActiveFields: activeFields,
                  setActiveFields,
                })
              }
              chartTitle="LTV"
            />
            <Spacer />

            <PortfolioDownloadCsvLink
              dataRows={dataRows}
              headers={headers}
              chartTitle="LTV"
              selectedTimePeriod={selectedTimePeriod}
            />
          </Flex>
        )}
      </PortfolioChartTemplateInstance>
      <PortfolioRiskTableTemplate
        portfolioRiskTableRows={riskTablesDataMap.collateralCovenantLtv}
        columns={riskTableColumns}
        tableTitle="Collateral Covenant LTV"
      />
      <PortfolioRiskTableTemplate
        portfolioRiskTableRows={riskTablesDataMap.netOutstandingLtv}
        columns={riskTableColumns}
        tableTitle="Net Outstanding LTV"
      />
      <PortfolioRiskTableTemplate
        portfolioRiskTableRows={riskTablesDataMap.performingLtv}
        columns={riskTableColumns}
        tableTitle="Performing LTV"
      />
      <PortfolioRiskTableTemplate
        portfolioRiskTableRows={
          riskTablesDataMap.chargedOffPercentageOfTotalExposure
        }
        columns={riskTableColumns}
        tableTitle="Charge Offs"
        creditAgreementArtifacts={parsedCreditAgreementFullArtifacts}
        creditFacilities={creditFacilities}
      />
      <PortfolioRiskTableTemplate
        portfolioRiskTableRows={
          riskTablesDataMap.delinquentPercentageOfTotalExposure
        }
        columns={riskTableColumns}
        tableTitle="Delinquencies"
        creditAgreementArtifacts={parsedCreditAgreementFullArtifacts}
        creditFacilities={creditFacilities}
      />
    </Box>
  );
});
